import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { Auth, Hub } from 'aws-amplify';
import { observer } from 'mobx-react-lite';
import React, { ReactNode, useEffect, useState } from 'react';
import { useStore } from 'stores/store';
import {
  authIdentify,
  getAuthDetails,
  getCookiesDomain,
  getCookiesSecured,
  getVerifiedUserDetails,
  IAuthDetails,
  isAuthConfigured,
  isAuthenticated,
  USER_DETAILS_VERSION
} from 'utils/authUtils';
import { AuthContext } from 'utils/context/AuthContext';
import { setCookie } from 'utils/cookies';

export const AuthProvider = ({
  children,
  authDetails
}: {
  children: ReactNode;
  authDetails?: IAuthDetails;
}) => {
  const [initial, setInitial] = useState<boolean>(true);
  const [authEvent, setAuthEvent] = useState<string>();
  const store = useStore();

  const [authState, setAuthState] = useState<IAuthDetails>({
    isAuthenticated: authDetails?.isAuthenticated,
    isApproved: authDetails?.isApproved,
    isManagement: authDetails?.isManagement,
    isSystem: authDetails?.isSystem,
    isSystemRW: authDetails?.isSystemRW,
    userId: authDetails?.userId,
    investorId: authDetails?.investorId,
    sponsorId: authDetails?.sponsorId,
    creatorId: authDetails?.creatorId,
    investorVerified: authDetails?.investorVerified,
    investorAccredited: authDetails?.investorAccredited,
    investorPending: authDetails?.investorPending,
    groups: authDetails?.groups,
    email: authDetails?.email,
    emailVerified: authDetails?.emailVerified,
    name: authDetails?.name,
    nickname: authDetails?.nickname,
    authToken: authDetails?.authToken,
    kycDone: authDetails?.kycDone
  });

  const updateState = async (newAuthState: IAuthDetails) => {
    if (
      authState.email !== newAuthState?.email ||
      !!authState.isAuthenticated !== !!newAuthState?.isAuthenticated ||
      !!authState.isApproved !== !!newAuthState?.isApproved ||
      !!authState.isManagement !== !!newAuthState?.isManagement ||
      !!authState.isSystem !== !!newAuthState?.isSystem ||
      !!authState.isSystemRW !== !!newAuthState?.isSystemRW ||
      (authState.name || '') !== (newAuthState?.name || '') ||
      (authState.nickname || '') !== (newAuthState?.nickname || '') ||
      (authState.userId || '') !== (newAuthState?.userId || '') ||
      (authState.investorId || '') !== (newAuthState?.investorId || '') ||
      (authState.sponsorId || '') !== (newAuthState?.sponsorId || '') ||
      (authState.creatorId || '') !== (newAuthState?.creatorId || '') ||
      (authState.authToken || '') !== (newAuthState?.authToken || '') ||
      !!authState.emailVerified !== !!newAuthState?.emailVerified ||
      !!authState.investorVerified !== !!newAuthState?.investorVerified ||
      !!authState.investorAccredited !== !!newAuthState?.investorAccredited ||
      !!authState.investorPending !== !!newAuthState?.investorPending ||
      !!authState.kycDone !== !!newAuthState?.kycDone
    ) {
      setAuthState((prevAuthState) => ({
        ...prevAuthState,
        ...newAuthState
      }));

      console.log('AuthProvider - new auth state', newAuthState);

      setCookie(
        'clientAuthState',
        { ...newAuthState },
        {
          domain: getCookiesDomain(),
          secure: getCookiesSecured(),
          path: '/'
        }
      );
      authIdentify(newAuthState);

      setInitial(false);
    } else if (initial) {
      authIdentify(newAuthState);

      setInitial(false);
    }
  };

  useEffect(() => {
    const setState = (session?: CognitoUserSession) => {
      if (isAuthenticated(session)) {
        const userData = getVerifiedUserDetails(session);
        if (userData?.version != USER_DETAILS_VERSION) {
          store.ProfileStore.fetchProfile().finally(() => {
            updateState(getAuthDetails(session));
          });
        } else {
          updateState({ ...getAuthDetails(session), kycDone: userData.kycDone });
        }
      } else {
        updateState(getAuthDetails(session));
      }
    };

    if (authEvent && isAuthConfigured()) {
      if (authEvent === 'signIn' || authEvent === 'init' || authEvent == 'tokenRefresh') {
        Auth.currentSession()
          .then((session: CognitoUserSession) => {
            setState(session);
          })
          .catch(() => {
            setState(undefined);
          });
      } else {
        setState(undefined);
      }
    }
    setAuthEvent(undefined);
  }, [authEvent]);

  useEffect(() => {
    if (authEvent && isAuthConfigured()) {
      Auth.currentSession().then((session: CognitoUserSession) => {
        const userData = getVerifiedUserDetails(session);
        updateState({ ...getAuthDetails(session), kycDone: userData?.kycDone });
      });
    }
  }, [store.ProfileStore.userData]);

  useEffect(() => {
    const cancelListen = Hub.listen('auth', ({ channel, payload }) => {
      if (channel == 'auth') {
        switch (payload.event) {
          case 'signIn':
          case 'tokenRefresh':
            setAuthEvent(payload.event);
            break;
          case 'signOut':
          case 'oAuthSignOut':
            // setTimeout(() => setAuthEvent(payload.event), 500);
            setAuthEvent(payload.event);
            break;
        }
      }
    });

    setAuthEvent('init');

    return cancelListen;
  }, []);

  const values = React.useMemo(() => authState, [authState]);

  // Finally, return the interface that we want to expose to our other components
  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};

export default observer(AuthProvider);
